import {
  map,
  switchMap,
  catchError,
  filter,
  takeUntil,
  scan,
  mergeMap,
} from 'rxjs/operators'
import { defer, of, Observable, interval } from 'rxjs'
import { Epic, ofType } from 'redux-observable'
import { StateObservable } from 'redux-observable'
import { IStoreState } from '../../IStoreState'
import {
  requestDashboard,
  RequestDashboard,
  requestDashboardFailure,
  requestDashboardSuccess,
} from '../../actions/dashboard/RequestDashboard'
import DashboardActionTypes from '../../actions/dashboard/DashboardActionTypes'
import { ColumnRepository } from '../../../../repositories/column/ColumnRepository'
import { ColumnApiDataHandler } from '../../../../data-handlers/column/ColumnApiDataHandler'
import {
  requestAvailableDays,
  RequestAvailableDays,
  requestAvailableDaysFailure,
  requestAvailableDaysSuccess,
  RequestCreateLabels,
  requestCreateLabelsFailure,
  RequestCreateLabelsSuccess,
  requestCreateLabelsSuccess,
  RequestReprint,
  requestReprintFailure,
  requestReprintSuccess,
  RequestOrderGotIt,
  requestOrderGotItFailure,
  requestOrderGotItSuccess,
  RequestOrderGotItSuccess,
  RequestOrderStatusChange,
  requestOrderStatusChangeFailure,
  RequestOrderStatusChangeSuccess,
  requestOrderStatusChangeSuccess,
  RequestProductAccept,
  requestProductAcceptFailure,
  RequestProductAcceptSuccess,
  requestProductAcceptSuccess,
  RequestProductAcceptFailure,
  RequestProductDecline,
  requestProductDeclineFailure,
  requestProductDeclineSuccess,
  RequestProductDeclineSuccess,
  RequestRemoveLabels,
  requestRemoveLabelsFailure,
  requestRemoveLabelsSuccess,
  RequestRemoveLabelsSuccess,
  requestVehicle,
  RequestVehicle,
  requestVehicleFailure,
  requestVehicleSuccess,
  setFailedOrderId,
  SetSelectedDate,
  RequestPrint,
  requestPrintSuccess,
  requestPrintFailure,
  RequestPrintSuccess,
  RequestPrintFailure,
  RequestSettings,
  requestSettingsSuccess,
  requestSettingsFailure,
  requestSettings,
  RequestSettingsSuccess,
  openConfirmDialog,
  ConfirmAcceptDeclinedProduct,
  requestProductAccept,
  RequestSettingsRestartInterval,
  setProductLoading,
} from '../../actions/dashboard/DashboardAction'
import { setNotification } from '../../actions/notifications/NotificationsAction'
import { Notification } from '../../../../entities/notification/Notification'
import { IntlService } from '../../../intl/IntlService'
import changeOrderStatusButtonMessages from '../../../../components/change-order-status-button/ChangeOrderStatusButton.messages'
import { printFailureNotification, refreshSelectedDay } from './printFailureActions'
import { applyServerDefaultLabelSize } from '../../../label-size/labelSize'
import { AvailableDaysApiDataHandler } from '../../../../data-handlers/available-days/AvailableDaysApiDataHandler'
import { AvailableDaysRepository } from '../../../../repositories/available-days/AvailableDaysRepository'
import { OrderApiDataHandler } from '../../../../data-handlers/order/OrderApiDataHandler'
import { OrderRepository } from '../../../../repositories/order/OrderRepository'
import { LabelRepository } from '../../../../repositories/label/LabelRepository'
import { LabelApiDataHandler } from '../../../../data-handlers/label/LabelApiDataHandler'
import FailedOrderReason from '../../../../constants/FailedOrderReason'
import AuthActionTypes from '../../actions/auth/AuthActionTypes'
import { VehicleRepository } from '../../../../repositories/vehicle/VehicleRepository'
import { VehicleDataHandler } from '../../../../data-handlers/vehicle/VehicleApiDataHandler'
import { OrderStatus } from '../../../../entities/order/Order'
import { SettingsRepository } from '../../../../repositories/settings/SettingsRepository'
import { SettingsDataHandler } from '../../../../data-handlers/settings/SettingsApiDataHandler'
import { ColumnNumber } from '../../../../entities/column/Column'
import { isSameDay, startOfDay } from 'date-fns'
import { dayTransitionEpics } from './dayTransition'

const columnRepository = new ColumnRepository(new ColumnApiDataHandler())
const orderRepository = new OrderRepository(new OrderApiDataHandler())
const availableDaysRepository = new AvailableDaysRepository(
  new AvailableDaysApiDataHandler()
)
const labelRepository = new LabelRepository(new LabelApiDataHandler())
const vehicleRepository = new VehicleRepository(new VehicleDataHandler())
const settingsRepository = new SettingsRepository(new SettingsDataHandler())

const vehiclesRequestInterval = 2 * 60 * 1000

const isTodaySelected = (state$: StateObservable<IStoreState>) =>
  isSameDay(state$.value.Dashboard.selectedDate, startOfDay(new Date()))

/**
* Map set date to request dashboard
*/
export const mapSetDateToRequestDashboardEpic = (
  action$: Observable<
      | SetSelectedDate
      | RequestProductAcceptSuccess
      | RequestProductDeclineSuccess
      | RequestPrintSuccess
      | RequestCreateLabelsSuccess
      | RequestRemoveLabelsSuccess
      | RequestOrderStatusChangeSuccess
      | RequestOrderGotItSuccess
  >,
  state$: StateObservable<IStoreState>
) =>
  action$.pipe(
      ofType(
          DashboardActionTypes.SET_SELECTED_DATE,
          DashboardActionTypes.REQUEST_PRODUCT_ACCEPT_SUCCESS,
          DashboardActionTypes.REQUEST_PRODUCT_DECLINE_SUCCESS,
          DashboardActionTypes.REQUEST_PRINT_SUCCESS,
          DashboardActionTypes.REQUEST_CREATE_LABELS_SUCCESS,
          DashboardActionTypes.REQUEST_REMOVE_LABELS_SUCCESS,
          DashboardActionTypes.REQUEST_ORDER_STATUS_CHANGE_SUCCESS,
          DashboardActionTypes.REQUEST_ORDER_GOT_IT_SUCCESS
      ),
      switchMap((action) =>
          of(
              requestDashboard({
                  date: state$.value.Dashboard.selectedDate,
                  shouldResetOpenCycles: [
                      DashboardActionTypes.SET_SELECTED_DATE,
                  ].includes(action.type),
              })
          )
      )
  )

/**
* Map set interval to request dashboard
*/
export const mapSetIntervalToRequestDashboardEpic = (
  action$: Observable<
      RequestSettingsSuccess | RequestSettingsRestartInterval
  >,
  state$: StateObservable<IStoreState>
) =>
  action$.pipe(
      ofType(
          DashboardActionTypes.REQUEST_SETTINGS_SUCCESS,
          DashboardActionTypes.RESTART_INTERVAL
      ),
      switchMap((action) =>
          interval(state$.value.Dashboard.refreshInterval).pipe(
              map(() => {
                  console.log('Interval triggered')
                  return requestDashboard({
                      date: state$.value.Dashboard.selectedDate,
                      shouldResetOpenCycles: [
                          DashboardActionTypes.SET_SELECTED_DATE,
                      ].includes(action.type),
                  })
              }),
              takeUntil(
                  action$.pipe(
                      filter(() => !state$.value.Auth.isLogged),
                      filter(
                          (newAction) =>
                              newAction.type ===
                              DashboardActionTypes.RESTART_INTERVAL
                      )
                  )
              )
          )
      )
  )

// export const mapSetIntervalToRequestDashboardEpic = (
//     action$: Observable<RequestSettingsSuccess>,
//     state$: StateObservable<IStoreState>
// ) =>
//     action$.pipe(
//         ofType(DashboardActionTypes.REQUEST_SETTINGS_SUCCESS),
//         switchMap((action) =>
//             interval(state$.value.Dashboard.refreshInterval).pipe(
//                 map(() =>
//                     requestDashboard({
//                         date: state$.value.Dashboard.selectedDate,
//                         shouldResetOpenCycles: [
//                             DashboardActionTypes.SET_SELECTED_DATE,
//                         ].includes(action.type),
//                     })
//                 ),
//                 takeUntil(
//                     action$.pipe(filter(() => !state$.value.Auth.isLogged))
//                 )
//             )
//         )
//     )

/**
* Request columns for dashboard
*/
export const requestDashboardEpic = (action$: Observable<RequestDashboard>) =>
  action$.pipe(
      ofType(DashboardActionTypes.REQUEST_DASHBOARD),
      switchMap((action) =>
          defer(() => columnRepository.getByDate(action.payload.date)).pipe(
              map((columns) =>
                  requestDashboardSuccess({
                      columns,
                      shouldResetOpenCycles:
                          action.payload.shouldResetOpenCycles,
                  })
              ),
              catchError((error) =>
                  of(
                      requestDashboardFailure({
                          date: action.payload.date,
                          error,
                      })
                  )
              )
          )
      )
  )

/**
* Map actions to requestVehicleEpic
*/
export const mapSetIntervalToRequestVehicleEpic: Epic = (
  action$,
  state$: StateObservable<IStoreState>
) =>
  action$.pipe(
      ofType('APP_INIT', AuthActionTypes.REQUEST_LOGIN_SUCCESS),
      filter(() => state$.value.Auth.isLogged),
      switchMap(() =>
          interval(vehiclesRequestInterval).pipe(
              filter(() => isTodaySelected(state$)),
              map(() => requestVehicle()),
              takeUntil(
                  action$.pipe(filter(() => !state$.value.Auth.isLogged))
              )
          )
      )
  )

export const mapToRequestVehicleEpic: Epic = (
  action$: Observable<SetSelectedDate>,
  state$: StateObservable<IStoreState>
) =>
  action$.pipe(
      ofType(DashboardActionTypes.SET_SELECTED_DATE),
      filter(() => isTodaySelected(state$)),
      switchMap(() => of(requestVehicle()))
  )

/**
* Request vehicle to pickup
*/
export const requestVehicleEpic = (
  action$: Observable<RequestVehicle>,
  state$: StateObservable<IStoreState>
) =>
  action$.pipe(
      ofType(DashboardActionTypes.REQUEST_VEHICLE),
      filter(() => isTodaySelected(state$)),
      switchMap(() =>
          defer(() => vehicleRepository.getVehicles()).pipe(
              takeUntil(action$.pipe(filter(() => !isTodaySelected(state$)))),
              map((vehicles) => {
                  console.log(vehicles)
                  return requestVehicleSuccess({ vehicles })
              }),
              catchError((error) =>
                  of(
                      requestVehicleFailure({
                          error,
                      })
                  )
              )
          )
      )
  )

/**
* Map actions to requestAvailableDaysEpic
*/
export const mapToRequestAvailableDaysEpic: Epic = (
  action$,
  state$: StateObservable<IStoreState>
) =>
  action$.pipe(
      ofType(DashboardActionTypes.REQUEST_DASHBOARD_SUCCESS),
      filter(() => state$.value.Auth.isLogged),
      switchMap(() => of(requestAvailableDays()))
  )

/**
* Request days with available orders for dashboard
*/
export const requestAvailableDaysEpic = (
  action$: Observable<RequestAvailableDays>
) =>
  action$.pipe(
      ofType(DashboardActionTypes.REQUEST_AVAILABLE_DAYS),
      switchMap(() =>
          defer(() => availableDaysRepository.get()).pipe(
              map((availableDays) =>
                  requestAvailableDaysSuccess({ availableDays })
              ),
              catchError((error) =>
                  of(
                      requestAvailableDaysFailure({
                          error,
                      })
                  )
              )
          )
      )
  )

/**
* Confirm accept declined product
*/
export const confirmAcceptDeclinedProductEpic = (
  action$: Observable<ConfirmAcceptDeclinedProduct>
) =>
  action$.pipe(
      ofType(DashboardActionTypes.CONFIRM_ACCEPT_DECLINED_PRODUCT),
      switchMap((action) =>
          defer(() =>
              orderRepository.changeStatus(
                  action.payload.product.order.orderId,
                  OrderStatus.ACCEPTED
              )
          ).pipe(
              map(() =>
                  requestProductAccept({
                      product: action.payload.product,
                      forceAccept: true,
                  })
              ),
              catchError((error) =>
                  of(
                      requestOrderStatusChangeFailure({
                          orderId: action.payload.product.order.orderId,
                          error,
                      })
                  )
              )
          )
      )
  )

/**
* Request accept a product
*/
export const requestProductAcceptEpic = (
  action$: Observable<RequestProductAccept>,
  state$: StateObservable<IStoreState>
) =>
  action$.pipe(
      ofType(DashboardActionTypes.REQUEST_PRODUCT_ACCEPT),
      switchMap((action) => {
          const acceptKey = `accept-${action.payload.product.id}`

          const isOrderReady = state$.value.Dashboard.columns[
              ColumnNumber.READY
          ]
              .getAllOrders()
              .some(
                  (order) =>
                      order.orderId === action.payload.product.order.orderId
              )

          const isProductDeclined = action.payload.product.isDeclined

          if (
              isOrderReady &&
              isProductDeclined &&
              !action.payload.forceAccept
          ) {
              console.log('Product accept: opening confirm dialog (early return)')
              return of(openConfirmDialog(action.payload.product, action.payload.onEnd))
          }

          console.log('Product accept: making API call')
          return defer(() =>
              orderRepository.acceptProduct(action.payload.product)
          ).pipe(
              map(() => {
                  console.log('Product accept: API call success')
                  if (action.payload.onEnd) {
                      action.payload.onEnd()
                  }
                  return requestProductAcceptSuccess({
                      product: action.payload.product,
                  })
              }),
              catchError((error) => {
                  console.log('Product accept: API call error', error)
                  if (action.payload.onEnd) {
                      action.payload.onEnd()
                  }
                  return of(
                      requestProductAcceptFailure({
                          product: action.payload.product,
                          error,
                      })
                  )
              })
          )
      })
  )

/**
* Request decline a product
*/
export const requestProductDeclineEpic = (
  action$: Observable<RequestProductDecline>
) =>
  action$.pipe(
      ofType(DashboardActionTypes.REQUEST_PRODUCT_DECLINE),
      switchMap((action) =>
          defer(() =>
              orderRepository.declineProduct(
                  action.payload.product,
                  action.payload.declineData
              )
          ).pipe(
              map(() =>
                  requestProductDeclineSuccess({
                      product: action.payload.product,
                  })
              ),
              catchError((error) =>
                  of(
                      requestProductDeclineFailure({
                          product: action.payload.product,
                          error,
                      })
                  )
              )
          )
      )
  )

/**
* Request print
*/
export const requestPrintEpic = (
  action$: Observable<RequestPrint>,
  state$: StateObservable<IStoreState>
) =>
  action$.pipe(
      ofType(DashboardActionTypes.REQUEST_PRINT),
      switchMap((action) =>
          defer(() => labelRepository.print(action.payload.product)).pipe(
              map(() => {
                  if (action.payload.onEnd) {
                      action.payload.onEnd()
                  }
                  return requestPrintSuccess({
                      product: action.payload.product,
                  })
              }),
              catchError((error) => {
                  if (action.payload.onEnd) {
                      action.payload.onEnd()
                  }
                  // Refreshed unconditionally rather than only on 'not found in order':
                  // handler.create() already created the label server-side before
                  // printDocument could fail, so on every failure the dashboard is a
                  // label behind until the next poll. A stale-data error is just one
                  // case of that, and a redundant fetch when creation itself failed is
                  // harmless.
                  return of(
                      requestPrintFailure({
                          product: action.payload.product,
                          error,
                      }),
                      printFailureNotification(error),
                      refreshSelectedDay(state$)
                  )
              })
          )
      )
  )

/**
* Request reprint
*/
export const requestReprintEpic = (action$: Observable<RequestReprint>) =>
  action$.pipe(
      ofType(DashboardActionTypes.REQUEST_REPRINT),
      switchMap((action) =>
          defer(() => labelRepository.rePrint(action.payload.product)).pipe(
              map(() =>
                  requestReprintSuccess({
                      product: action.payload.product,
                  })
              ),
              // No dashboard refresh here, unlike print and create-labels: a reprint
              // reuses the existing label and creates nothing, so there is no new
              // server state for the dashboard to be behind on.
              catchError((error) =>
                  of(
                      requestReprintFailure({
                          product: action.payload.product,
                          error,
                      }),
                      printFailureNotification(error)
                  )
              )
          )
      )
  )

/**
* Request create labels
*/
export const requestCreateLabelsEpic = (
  action$: Observable<RequestCreateLabels>,
  state$: StateObservable<IStoreState>
) =>
  action$.pipe(
      ofType(DashboardActionTypes.REQUEST_CREATE_LABELS),
      switchMap((action) =>
          defer(() =>
              labelRepository.createBatch(action.payload.products)
          ).pipe(
              map(() =>
                  requestCreateLabelsSuccess({
                      products: action.payload.products,
                  })
              ),
              // Refreshed for the same reason as print: handler.createBatch() already
              // created the labels server-side before printDocument could fail, so
              // without this the seller sees no toast-worthy change and no new labels
              // until the next poll — it looks as though nothing happened at all.
              catchError((error) =>
                  of(
                      requestCreateLabelsFailure({
                          products: action.payload.products,
                          error,
                      }),
                      printFailureNotification(error),
                      refreshSelectedDay(state$)
                  )
              )
          )
      )
  )

/**
* Request remove labels
*/
export const requestRemoveLabelsEpic = (
  action$: Observable<RequestRemoveLabels>
) =>
  action$.pipe(
      ofType(DashboardActionTypes.REQUEST_REMOVE_LABELS),
      switchMap((action) =>
          defer(() =>
              labelRepository.removeBatch(action.payload.products)
          ).pipe(
              map(() =>
                  requestRemoveLabelsSuccess({
                      products: action.payload.products,
                  })
              ),
              catchError((error) =>
                  of(
                      requestRemoveLabelsFailure({
                          products: action.payload.products,
                          error,
                      })
                  )
              )
          )
      )
  )

/**
* Request order status change
*/
export const requestOrderStatusChangeEpic = (
  action$: Observable<RequestOrderStatusChange>,
  state$: StateObservable<IStoreState>
) =>
  action$.pipe(
      ofType(DashboardActionTypes.REQUEST_ORDER_STATUS_CHANGE),
      switchMap((action) => {
          if (action.payload.nextStatus !== OrderStatus.CANCELED) {
              const isNotAllProductsPrepared = state$.value.Dashboard.columns[
                  ColumnNumber.IN_PROGRESS
              ]
                  .getAllOrders()
                  .find((order) => order.orderId === action.payload.orderId)
                  ?.products.some(
                      (product) =>
                          !product.isLabeled &&
                          !product.isCanceled &&
                          !product.isDeclined
                  )

              if (isNotAllProductsPrepared) {
                  console.log('return 2')

                  if (action.payload.onEnd) {
                      action.payload.onEnd()
                  }

                  return of(
                      setFailedOrderId(
                          action.payload.orderId,
                          FailedOrderReason.NOT_ALL_PRODUCTS_PREPARED
                      )
                  )
              }

              const isNotAllProductsProcessed =
                  state$.value.Dashboard.columns[ColumnNumber.INCOMING]
                      .getAllOrders()
                      .find(
                          (order) => order.orderId === action.payload.orderId
                      )
                      ?.products.some(
                          (product) =>
                              !product.isCanceled && !product.isDeclined
                      )

              if (isNotAllProductsProcessed) {
                  console.log('return 1')

                  if (action.payload.onEnd) {
                      action.payload.onEnd()
                  }

                  return of(
                      setFailedOrderId(
                          action.payload.orderId,
                          FailedOrderReason.NOT_ALL_PRODUCTS_PROCESSED
                      )
                  )
              }
          }

          console.log('return 3')

          return defer(() =>
              orderRepository
                  .changeStatus(
                      action.payload.orderId,
                      action.payload.nextStatus
                  )
                  .finally(() => {
                      if (action.payload.onEnd) {
                          action.payload.onEnd()
                      }
                  })
          ).pipe(
              map(() =>
                  requestOrderStatusChangeSuccess({
                      orderId: action.payload.orderId,
                  })
              ),
              catchError((error) =>
                  of(
                      requestOrderStatusChangeFailure({
                          orderId: action.payload.orderId,
                          error,
                      })
                  )
              )
          )
      })
  )

/**
* Request order status change
*/
export const requestOrderGotItEpic = (action$: Observable<RequestOrderGotIt>) =>
  action$.pipe(
      ofType(DashboardActionTypes.REQUEST_ORDER_GOT_IT),
      switchMap((action) => {
          return defer(() =>
              orderRepository
                  .gotIt(
                      action.payload.orderId,
                      action.payload.itemId,
                      action.payload.customWay
                  )
                  .finally(() => {
                      if (action.payload.onEnd) {
                          action.payload.onEnd()
                      }
                  })
          ).pipe(
              map(() =>
                  requestOrderGotItSuccess({
                      orderId: action.payload.orderId,
                  })
              ),
              catchError((error) =>
                  of(
                      requestOrderGotItFailure({
                          orderId: action.payload.orderId,
                          error,
                      })
                  )
              )
          )
      })
  )

/**
* Map actions to requestDashboardSettingsEpic
*/
export const mapToRequestDashboardSettingsEpic: Epic = (
  action$,
  state$: StateObservable<IStoreState>
) =>
  action$.pipe(
      ofType(
          AuthActionTypes.REQUEST_LOGIN_SUCCESS,
          AuthActionTypes.SET_IS_LOGGED
      ),
      filter(() => state$.value.Auth.isLogged),
      switchMap(() => of(requestSettings()))
  )

/**
* Request dashboard settings
*/
export const requestDashboardSettingsEpic = (
  action$: Observable<RequestSettings>
) =>
  action$.pipe(
      ofType(DashboardActionTypes.REQUEST_SETTINGS),
      switchMap(() =>
          defer(() => settingsRepository.requestSettings()).pipe(
              map(({ storeName, refreshInterval, defaultLanguage, labelSize }) => {
                  // Seeded here rather than held in Redux because the label request is
                  // made from a module-scope data handler with no store access. Only
                  // fills an empty slot, so this poll cannot reset a seller's choice.
                  applyServerDefaultLabelSize(labelSize)

                  return requestSettingsSuccess({ storeName, refreshInterval, defaultLanguage })
              }),
              catchError((error) =>
                  of(
                      requestSettingsFailure({
                          error,
                      })
                  )
              )
          )
      )
  )

/**
* Clear product accept loading state after success or failure
*/
export const clearProductAcceptLoadingEpic = (
  action$: Observable<RequestProductAcceptSuccess | RequestProductAcceptFailure>
) =>
  action$.pipe(
      ofType(
          DashboardActionTypes.REQUEST_PRODUCT_ACCEPT_SUCCESS,
          DashboardActionTypes.REQUEST_PRODUCT_ACCEPT_FAILURE
      ),
      switchMap((action) => {
          const acceptKey = `accept-${action.payload.product.id}`
          return new Observable(subscriber => {
              setTimeout(() => {
                  subscriber.next(setProductLoading({ productKey: acceptKey, isLoading: false }))
                  subscriber.complete()
              }, 2000)
          })
      })
  )

/**
* Emit a one-time notification when an order's final PO transitions from missing → received
* (observed across successive REQUEST_DASHBOARD_SUCCESS emissions).
*/
export const notifyFinalPoReceivedEpic = (
  action$: Observable<{ type: string; payload: { columns: any[] } }>
) =>
  action$.pipe(
      ofType(DashboardActionTypes.REQUEST_DASHBOARD_SUCCESS),
      scan<any, { prev: Map<number, boolean> | null; newlyReceived: number[] }>(
          (acc, action) => {
              const curr = new Map<number, boolean>()
              for (const column of action.payload.columns) {
                  for (const order of column.getAllOrders()) {
                      curr.set(order.orderId, order.hasFinalPo())
                  }
              }
              const newlyReceived: number[] = []
              if (acc.prev) {
                  curr.forEach((hasPo, orderId) => {
                      if (hasPo && acc.prev!.get(orderId) === false) {
                          newlyReceived.push(orderId)
                      }
                  })
              }
              // Stash latest snapshot back into prev for next round
              return { prev: curr, newlyReceived }
          },
          { prev: null, newlyReceived: [] }
      ),
      mergeMap(({ newlyReceived }) =>
          newlyReceived.map((orderId) =>
              setNotification(
                  new Notification({
                      id: `final-po-${orderId}-${Date.now()}`,
                      text: IntlService.formatMessage(
                          changeOrderStatusButtonMessages.finalPoReceivedToast,
                          { orderId }
                      ),
                  })
              )
          )
      )
  )

/**
* Clear product print loading state after success or failure
*/
export const clearProductPrintLoadingEpic = (
  action$: Observable<RequestPrintSuccess | RequestPrintFailure>
) =>
  action$.pipe(
      ofType(
          DashboardActionTypes.REQUEST_PRINT_SUCCESS,
          DashboardActionTypes.REQUEST_PRINT_FAILURE
      ),
      switchMap((action) => {
          const printKey = `print-${action.payload.product.id}`
          return new Observable(subscriber => {
              setTimeout(() => {
                  subscriber.next(setProductLoading({ productKey: printKey, isLoading: false }))
                  subscriber.complete()
              }, 2000)
          })
      })
  )

export const dashboardEpics = [
  mapToRequestAvailableDaysEpic,
  requestAvailableDaysEpic,
  mapSetDateToRequestDashboardEpic,
  mapSetIntervalToRequestDashboardEpic,
  requestDashboardEpic,

  mapSetIntervalToRequestVehicleEpic,
  mapToRequestVehicleEpic,
  requestVehicleEpic,

  confirmAcceptDeclinedProductEpic,
  requestProductAcceptEpic,
  requestProductDeclineEpic,
  requestPrintEpic,
  requestReprintEpic,
  requestCreateLabelsEpic,
  requestRemoveLabelsEpic,

  requestOrderStatusChangeEpic,
  requestOrderGotItEpic,

  mapToRequestDashboardSettingsEpic,
  requestDashboardSettingsEpic,

  // Product loading state epics
  clearProductAcceptLoadingEpic,
  clearProductPrintLoadingEpic,

  // Final-PO received toast
  notifyFinalPoReceivedEpic,

  // Day transition epics
  ...dayTransitionEpics,
]
